import React, { useState, useEffect } from 'react';
import { axiosInstance as axios } from '../../../context/AuthContext';
import { FaTimes, FaEnvelope, FaCheck, FaClock, FaExclamationTriangle, FaUserClock, FaBan, FaRedo, FaCopy, FaUsers, FaHome } from 'react-icons/fa';
import { toast } from 'sonner';

const API = '/api';

const STATUS_CONFIG = {
  pending: {
    label: 'Pending',
    icon: FaClock,
    color: 'bg-amber-100 text-amber-700 border-amber-300',
    description: 'Sent — waiting for recipient to accept',
  },
  awaiting_approval: {
    label: 'Awaiting your approval',
    icon: FaUserClock,
    color: 'bg-blue-100 text-blue-700 border-blue-300',
    description: 'Recipient created an account — needs owner approval',
  },
  accepted: {
    label: 'Accepted',
    icon: FaCheck,
    color: 'bg-green-100 text-green-700 border-green-300',
    description: 'Joined the family',
  },
  expired: {
    label: 'Expired',
    icon: FaExclamationTriangle,
    color: 'bg-gray-100 text-gray-700 border-gray-300',
    description: 'Invitation expired before being accepted — resend to renew',
  },
  rejected: {
    label: 'Rejected',
    icon: FaBan,
    color: 'bg-red-100 text-red-700 border-red-300',
    description: 'Owner declined this account request',
  },
  joined: {
    label: 'Joined Legacy Forge',
    icon: FaCheck,
    color: 'bg-green-100 text-green-700 border-green-300',
    description: 'They created their own family on Legacy Forge',
  },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch {
    return String(dateStr);
  }
};

export function SentInvitationsModal({ isOpen, onClose, familyId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    invitations: [], counts: {}, family_referrals: [], referral_counts: {}, family_name: ''
  });
  const [activeTab, setActiveTab] = useState('members'); // 'members' | 'other_families'
  const [filter, setFilter] = useState('all');
  const [resendingId, setResendingId] = useState(null);

  useEffect(() => {
    if (isOpen && familyId) fetchInvitations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, familyId]);

  // Reset filter when switching tabs
  useEffect(() => { setFilter('all'); }, [activeTab]);

  const fetchInvitations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/families/${familyId}/sent-invitations`, { withCredentials: true });
      setData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load sent invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (inv) => {
    if (!inv.proxy_id) {
      toast.error('Cannot resend — no proxy member record found');
      return;
    }
    setResendingId(inv.invitation_id);
    try {
      const res = await axios.post(
        `${API}/invitations/send-to-proxy`,
        { proxy_id: inv.proxy_id, custom_message: null },
        { withCredentials: true }
      );
      if (res.data.email_sent) {
        toast.success(`Invitation resent to ${inv.recipient_email}`);
      } else if (res.data.invitation_link) {
        try { await navigator.clipboard.writeText(res.data.invitation_link); } catch (_) {}
        toast.info('Email could not send — invitation link copied to clipboard');
      } else {
        toast.info(res.data.message || 'Invitation resent');
      }
      fetchInvitations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invitation');
    } finally {
      setResendingId(null);
    }
  };

  if (!isOpen) return null;

  const referralCount = data.referral_counts?.total || 0;
  const memberCount = data.counts?.total || 0;

  // Member invitations filtering
  const filteredMemberInvitations = filter === 'all'
    ? data.invitations
    : data.invitations.filter(i => i.status === filter);

  // Family referrals filtering
  const filteredReferrals = filter === 'all'
    ? data.family_referrals
    : data.family_referrals.filter(i => i.status === filter);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="sent-invitations-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <FaEnvelope className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Sent Invitations</h2>
              <p className="text-sm text-gray-500">{data.family_name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="close-sent-invitations">
            <FaTimes className="text-gray-500" />
          </button>
        </div>

        {/* Tab navigation */}
        {!loading && (
          <div className="flex border-b border-gray-200 px-6">
            <TabButton
              icon={FaUsers}
              label="Family Members"
              count={memberCount}
              active={activeTab === 'members'}
              onClick={() => setActiveTab('members')}
              testId="tab-members-invitations"
            />
            <TabButton
              icon={FaHome}
              label="Other Families"
              count={referralCount}
              active={activeTab === 'other_families'}
              onClick={() => setActiveTab('other_families')}
              testId="tab-other-families-invitations"
            />
          </div>
        )}

        {/* Filter pills */}
        {!loading && activeTab === 'members' && data.invitations.length > 0 && (
          <div className="px-6 pt-4 pb-2 border-b border-gray-100 flex flex-wrap gap-2">
            <FilterPill label={`All (${data.counts.total || 0})`} active={filter === 'all'} onClick={() => setFilter('all')} />
            {data.counts.pending > 0 && (
              <FilterPill label={`Pending (${data.counts.pending})`} active={filter === 'pending'} onClick={() => setFilter('pending')} />
            )}
            {data.counts.awaiting_approval > 0 && (
              <FilterPill label={`Awaiting approval (${data.counts.awaiting_approval})`} active={filter === 'awaiting_approval'} onClick={() => setFilter('awaiting_approval')} />
            )}
            {data.counts.accepted > 0 && (
              <FilterPill label={`Accepted (${data.counts.accepted})`} active={filter === 'accepted'} onClick={() => setFilter('accepted')} />
            )}
            {data.counts.expired > 0 && (
              <FilterPill label={`Expired (${data.counts.expired})`} active={filter === 'expired'} onClick={() => setFilter('expired')} />
            )}
            {data.counts.rejected > 0 && (
              <FilterPill label={`Rejected (${data.counts.rejected})`} active={filter === 'rejected'} onClick={() => setFilter('rejected')} />
            )}
          </div>
        )}

        {!loading && activeTab === 'other_families' && data.family_referrals.length > 0 && (
          <div className="px-6 pt-4 pb-2 border-b border-gray-100 flex flex-wrap gap-2">
            <FilterPill label={`All (${referralCount})`} active={filter === 'all'} onClick={() => setFilter('all')} />
            {data.referral_counts.pending > 0 && (
              <FilterPill label={`Pending (${data.referral_counts.pending})`} active={filter === 'pending'} onClick={() => setFilter('pending')} />
            )}
            {data.referral_counts.joined > 0 && (
              <FilterPill label={`Joined (${data.referral_counts.joined})`} active={filter === 'joined'} onClick={() => setFilter('joined')} />
            )}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-3 text-gray-600 text-sm">Loading invitation history...</p>
            </div>
          ) : activeTab === 'members' ? (
            data.invitations.length === 0 ? (
              <EmptyState type="members" />
            ) : filteredMemberInvitations.length === 0 ? (
              <div className="text-center py-12 text-gray-500">No invitations match this filter.</div>
            ) : (
              <div className="space-y-3">
                {filteredMemberInvitations.map(inv => (
                  <InvitationRow
                    key={inv.invitation_id}
                    inv={inv}
                    resendingId={resendingId}
                    onResend={() => handleResend(inv)}
                  />
                ))}
              </div>
            )
          ) : (
            data.family_referrals.length === 0 ? (
              <EmptyState type="other_families" />
            ) : filteredReferrals.length === 0 ? (
              <div className="text-center py-12 text-gray-500">No referrals match this filter.</div>
            ) : (
              <div className="space-y-3">
                {filteredReferrals.map(ref => (
                  <ReferralRow key={ref.referral_id} ref_item={ref} />
                ))}
              </div>
            )
          )}
        </div>

        {/* Footer with delivery tip */}
        {!loading && (memberCount > 0 || referralCount > 0) && (
          <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl text-xs text-gray-600">
            <FaEnvelope className="inline mr-2 text-gray-400" />
            <span>
              Delivery confirmation: This list shows whether the recipient accepted. To confirm
              email delivery itself (delivered / bounced / spam), check your{' '}
              <a href="https://resend.com/emails" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                Resend dashboard
              </a>.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function TabButton({ icon: Icon, label, count, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition ${
        active
          ? 'border-blue-600 text-blue-700'
          : 'border-transparent text-gray-600 hover:text-gray-900'
      }`}
    >
      <Icon className="text-base" />
      <span>{label}</span>
      <span className={`text-xs px-2 py-0.5 rounded-full ${active ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
        {count}
      </span>
    </button>
  );
}

function FilterPill({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
        active
          ? 'bg-blue-600 text-white shadow-sm'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );
}

function EmptyState({ type = 'members' }) {
  if (type === 'other_families') {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FaHome className="text-gray-400 text-2xl" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No family referrals sent yet</h3>
        <p className="text-gray-600 text-sm max-w-sm mx-auto">
          When you invite other potential families to try Legacy Forge, they'll appear here
          with their join status.
        </p>
      </div>
    );
  }
  return (
    <div className="text-center py-12">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <FaEnvelope className="text-gray-400 text-2xl" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">No member invitations sent yet</h3>
      <p className="text-gray-600 text-sm max-w-sm mx-auto">
        When you invite proxy members to create accounts, they'll appear here with their
        delivery and acceptance status.
      </p>
    </div>
  );
}

function ReferralRow({ ref_item }) {
  const cfg = STATUS_CONFIG[ref_item.status] || STATUS_CONFIG.pending;
  const StatusIcon = cfg.icon;

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(ref_item.recipient_email);
      toast.success('Email copied to clipboard');
    } catch {
      toast.error('Could not copy email');
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition" data-testid={`referral-row-${ref_item.referral_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h4 className="font-semibold text-gray-900 truncate">{ref_item.recipient_name || 'Unnamed'}</h4>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
              <StatusIcon className="text-xs" />
              {cfg.label}
            </span>
            {ref_item.referral_code && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-purple-50 text-purple-700 border border-purple-200">
                {ref_item.referral_code}
              </span>
            )}
          </div>
          <button
            onClick={copyEmail}
            className="text-sm text-gray-600 hover:text-blue-600 inline-flex items-center gap-1.5 mb-2"
            title="Click to copy"
          >
            {ref_item.recipient_email}
            <FaCopy className="text-xs opacity-50" />
          </button>
          <p className="text-xs text-gray-500 italic">{cfg.description}</p>

          {ref_item.message && (
            <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-700 italic border-l-2 border-gray-300">
              "{ref_item.message}"
            </div>
          )}

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
            <div>
              <span className="text-gray-400">Sent:</span> {formatDate(ref_item.sent_at)}
            </div>
            <div>
              <span className="text-gray-400">Sent by:</span> {ref_item.invited_by_name || '—'}
              {ref_item.is_my_invitation && <span className="ml-1 text-blue-600">(you)</span>}
            </div>
            <div>
              <span className="text-gray-400">Email delivery:</span>{' '}
              {ref_item.email_sent ? (
                <span className="text-green-700">Sent ✓</span>
              ) : (
                <span className="text-amber-700">Not sent — link only</span>
              )}
            </div>
            {ref_item.completed_at && (
              <div>
                <span className="text-gray-400">Joined:</span> {formatDate(ref_item.completed_at)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function InvitationRow({ inv, resendingId, onResend }) {
  const cfg = STATUS_CONFIG[inv.status] || STATUS_CONFIG.pending;
  const StatusIcon = cfg.icon;
  const canResend = inv.status === 'pending' || inv.status === 'expired';

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(inv.recipient_email);
      toast.success('Email copied to clipboard');
    } catch {
      toast.error('Could not copy email');
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition" data-testid={`invitation-row-${inv.invitation_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold text-gray-900 truncate">{inv.recipient_name || 'Unnamed'}</h4>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
              <StatusIcon className="text-xs" />
              {cfg.label}
            </span>
          </div>
          <button
            onClick={copyEmail}
            className="text-sm text-gray-600 hover:text-blue-600 inline-flex items-center gap-1.5 mb-2"
            title="Click to copy"
          >
            {inv.recipient_email}
            <FaCopy className="text-xs opacity-50" />
          </button>
          <p className="text-xs text-gray-500 italic">{cfg.description}</p>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
            <div>
              <span className="text-gray-400">Sent:</span> {formatDate(inv.sent_at)}
            </div>
            <div>
              <span className="text-gray-400">Sent by:</span> {inv.invited_by_name || '—'}
              {inv.is_my_invitation && <span className="ml-1 text-blue-600">(you)</span>}
            </div>
            {inv.status === 'pending' && (
              <div>
                <span className="text-gray-400">Expires:</span> {formatDate(inv.expires_at)}
              </div>
            )}
            {inv.accepted_at && (
              <div>
                <span className="text-gray-400">Accepted:</span> {formatDate(inv.accepted_at)}
              </div>
            )}
            {inv.approved_at && (
              <div className="sm:col-span-2">
                <span className="text-gray-400">Approved by:</span>{' '}
                {inv.approved_by_name || 'owner'} on {formatDate(inv.approved_at)}
              </div>
            )}
            {inv.rejection_reason && (
              <div className="sm:col-span-2 text-red-600">
                <span className="text-gray-400">Reason:</span> {inv.rejection_reason}
              </div>
            )}
          </div>
        </div>

        {canResend && (
          <button
            onClick={onResend}
            disabled={resendingId === inv.invitation_id}
            className="flex-shrink-0 inline-flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            data-testid={`resend-${inv.invitation_id}`}
          >
            <FaRedo className={resendingId === inv.invitation_id ? 'animate-spin' : ''} />
            <span>{resendingId === inv.invitation_id ? 'Sending...' : 'Resend'}</span>
          </button>
        )}
      </div>
    </div>
  );
}
